import { Direction } from '../sim/direction';

export interface MobileJoystick {
  direction(): Direction | null;
  release(): void;
}

export const JOYSTICK_DEAD_ZONE = 0.24;

/** Resolve a screen-space joystick vector to one of the game's eight facings. */
export function directionFromJoystick(
  dx: number,
  dy: number,
  radius: number,
): Direction | null {
  if (!(radius > 0)) return null;
  const distance = Math.hypot(dx, dy);
  if (distance / radius < JOYSTICK_DEAD_ZONE) return null;

  const octant = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  const directions = [
    Direction.E,
    Direction.SE,
    Direction.S,
    Direction.SW,
    Direction.W,
    Direction.NW,
    Direction.N,
    Direction.NE,
  ] as const;
  return directions[(octant + 8) % 8]!;
}

/**
 * Wire the touch-only movement control declared in `index.html`.
 *
 * The control owns only a live direction.
 * `main.ts` samples it beside keyboard and mouse input, preserving the single
 * prediction-walker movement seam.
 */
export function initMobileJoystick(doc: Document = document): MobileJoystick {
  const base = doc.getElementById('movement-joystick');
  const stick = doc.getElementById('movement-stick');
  let activePointer: number | undefined;
  let liveDirection: Direction | null = null;

  const release = (): void => {
    activePointer = undefined;
    liveDirection = null;
    base?.removeAttribute('data-active');
    if (stick) stick.style.transform = 'translate(0px, 0px)';
  };

  if (!base || !stick) return { direction: () => liveDirection, release };

  const update = (e: PointerEvent): void => {
    const rect = base.getBoundingClientRect();
    const radius = Math.min(rect.width, rect.height) / 2;
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    const distance = Math.hypot(dx, dy);
    const visualRadius = Math.max(0, radius - stick.getBoundingClientRect().width / 2 - 4);
    const scale = distance > visualRadius && distance > 0 ? visualRadius / distance : 1;
    const visualX = dx * scale;
    const visualY = dy * scale;
    stick.style.transform = `translate(${visualX}px, ${visualY}px)`;
    liveDirection = directionFromJoystick(dx, dy, radius);
  };

  base.addEventListener('pointerdown', (e) => {
    if (activePointer !== undefined || !e.isPrimary) return;
    activePointer = e.pointerId;
    base.setAttribute('data-active', '');
    base.setPointerCapture?.(e.pointerId);
    update(e);
    e.preventDefault();
  });
  base.addEventListener('pointermove', (e) => {
    if (e.pointerId !== activePointer) return;
    update(e);
    e.preventDefault();
  });
  const end = (e: PointerEvent): void => {
    if (e.pointerId !== activePointer) return;
    base.releasePointerCapture?.(e.pointerId);
    release();
    e.preventDefault();
  };
  base.addEventListener('pointerup', end);
  base.addEventListener('pointercancel', end);
  doc.defaultView?.addEventListener('blur', release);
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') release();
  });

  return { direction: () => liveDirection, release };
}
